/**
 * Locale-aware formatting.
 *
 * Everything here goes through `Intl` and the browser's own locale, so a
 * Norwegian browser gets "13:04" and an American one "1:04 PM" without the
 * application carrying a translation table for dates, and without anybody
 * hand-rolling calendar arithmetic.
 */

export type DateLike = Date | string | number

const DAY_MS = 86_400_000

// Formatters are expensive to construct and message lists build thousands of
// timestamps per scroll, so each distinct option set is made exactly once.
const dateTimeFormatters = new Map<string, Intl.DateTimeFormat>()
const numberFormatters = new Map<string, Intl.NumberFormat>()
let relativeFormatter: Intl.RelativeTimeFormat | null = null

function dateTimeFormat(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = JSON.stringify(options)
  const cached = dateTimeFormatters.get(key)
  if (cached !== undefined) return cached

  const created = new Intl.DateTimeFormat(undefined, options)
  dateTimeFormatters.set(key, created)
  return created
}

function numberFormat(options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = JSON.stringify(options)
  const cached = numberFormatters.get(key)
  if (cached !== undefined) return cached

  const created = new Intl.NumberFormat(undefined, options)
  numberFormatters.set(key, created)
  return created
}

function relativeFormat(): Intl.RelativeTimeFormat {
  // `numeric: 'auto'` is what turns "1 day ago" into "yesterday" and "in 0
  // seconds" into "now", in whatever language the browser is set to.
  // The administration UI is intentionally English regardless of the
  // browser's locale; avoid rendering values such as Norwegian “nå”.
  relativeFormatter ??= new Intl.RelativeTimeFormat('en', { numeric: 'auto' })
  return relativeFormatter
}

/** Null rather than an Invalid Date, so every caller has one thing to check. */
function toDate(value: DateLike): Date | null {
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
}

/** Whole calendar days from `from` to `to`, in the viewer's own time zone. */
function calendarDaysBetween(from: Date, to: Date): number {
  // Rounding absorbs the 23- and 25-hour days that daylight saving produces.
  return Math.round((startOfDay(to) - startOfDay(from)) / DAY_MS)
}

function capitalise(text: string): string {
  const first = text.charAt(0)
  return first === '' ? text : first.toLocaleUpperCase() + text.slice(1)
}

function roundTo(value: number, digits: number): number {
  const factor = 10 ** digits
  return Math.round(value * factor) / factor
}

// SI rather than binary units: a file manager, a browser download bar and an
// object store all report 1.4 MB for the same 1 400 000 bytes.
const BYTE_UNITS = ['byte', 'kilobyte', 'megabyte', 'gigabyte', 'terabyte', 'petabyte'] as const

/** `1.4 MB`, `184 kB`, `512 byte`. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes)) return ''

  const sign = bytes < 0 ? -1 : 1
  let value = Math.abs(bytes)
  let step = 0

  while (value >= 1000 && step < BYTE_UNITS.length - 1) {
    value /= 1000
    step += 1
  }

  let digits = step === 0 ? 0 : value < 100 ? 1 : 0

  // Rounding can carry a value into the next unit — 999 960 bytes would read
  // "1000 kB" — so the carry is applied before the unit is chosen.
  if (roundTo(value, digits) >= 1000 && step < BYTE_UNITS.length - 1) {
    value /= 1000
    step += 1
    digits = 1
  }

  const unit = BYTE_UNITS[step] ?? 'byte'

  return numberFormat({
    style: 'unit',
    unit,
    unitDisplay: 'short',
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  }).format(sign * value)
}

/**
 * The short word for what a file is — `PDF`, `PNG`, `Word` — for the line
 * under a filename on an attachment card.
 *
 * The extension is preferred over the declared mime type because the mime type
 * is whatever the uploading client said it was, and a wrong one would put a
 * confident wrong word on the card. Nothing here decides anything about how a
 * file is handled; that is `AttachmentView`'s allowlist, on the real type.
 */
const NAMED_TYPES: Readonly<Record<string, string>> = {
  'application/pdf': 'PDF',
  'application/msword': 'Word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
  'application/vnd.ms-excel': 'Excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
  'application/vnd.ms-powerpoint': 'PowerPoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint',
  'application/zip': 'ZIP',
  'application/gzip': 'GZIP',
  'text/plain': 'Text',
  'text/csv': 'CSV',
  'text/markdown': 'Markdown',
}

export function formatFileKind(filename: string, mimeType: string): string {
  const type = (mimeType.split(';')[0] ?? '').trim().toLowerCase()
  const named = NAMED_TYPES[type]
  if (named !== undefined) return named

  const dot = filename.lastIndexOf('.')
  const extension = dot > 0 ? filename.slice(dot + 1) : ''
  if (/^[a-z0-9]{1,8}$/i.test(extension)) return extension.toUpperCase()

  const family = type.split('/')[0] ?? ''
  if (family === 'image' || family === 'video' || family === 'audio') {
    return `${family.charAt(0).toUpperCase()}${family.slice(1)}`
  }

  return 'File'
}

/** Clock time only: `13:04`. */
// Binary, and deliberately not localised: these strings have to read as the
// same number the server prints in `Relaii.Storage.Quota.humanize/1`, that
// `relaii doctor` prints, and that `docs/storage.md` documents. Formatting an
// operator's capacity with `formatBytes` above would divide by 1000 and show
// the same disk about 7% larger under an identical "GB" label — and an operator
// who set RELAII_STORAGE_MAX_GB=100 would read it back as 107.
const CAPACITY_UNITS: readonly (readonly [number, string])[] = [
  [1024 ** 4, 'TB'],
  [1024 ** 3, 'GB'],
  [1024 ** 2, 'MB'],
  [1024, 'kB'],
]

/**
 * `40.0 GB`, `2.0 kB`, `512 B` — the server's own rendering, for the figures a
 * panel derives itself and the server therefore never sends a `*_human` for.
 * Prefer the server's string wherever there is one.
 */
export function formatCapacity(bytes: number): string {
  if (!Number.isFinite(bytes)) return ''

  for (const [divisor, unit] of CAPACITY_UNITS) {
    if (bytes >= divisor) return `${(bytes / divisor).toFixed(1)} ${unit}`
  }

  return `${String(Math.trunc(bytes))} B`
}

export function formatTime(value: DateLike): string {
  const date = toDate(value)
  return date === null ? '' : dateTimeFormat({ timeStyle: 'short' }).format(date)
}

/**
 * The clock where somebody else is: `09:24`.
 *
 * The zone is a string from the server, and for a federated account it came
 * from a machine this one does not administer, so `Intl` is allowed to reject
 * it — an unknown zone throws a RangeError rather than falling back to UTC.
 * Null means "do not show a local time", which is the right answer both for a
 * zone nobody set and for one that does not exist.
 */
export function formatTimeInZone(value: DateLike, timeZone: string): string | null {
  const date = toDate(value)
  if (date === null || timeZone.trim() === '') return null

  try {
    return new Intl.DateTimeFormat(undefined, { timeStyle: 'short', timeZone }).format(date)
  } catch {
    return null
  }
}

/** Day heading: `Today`, `Yesterday`, otherwise `30 Aug 2026`. */
export function formatDay(value: DateLike, now: DateLike = Date.now()): string {
  const date = toDate(value)
  const reference = toDate(now)
  if (date === null || reference === null) return ''

  const days = calendarDaysBetween(date, reference)
  if (Math.abs(days) <= 1) return capitalise(relativeFormat().format(-days, 'day'))

  return dateTimeFormat({ day: 'numeric', month: 'short', year: 'numeric' }).format(date)
}

const DIVISIONS: ReadonlyArray<{ amount: number; unit: Intl.RelativeTimeFormatUnit }> = [
  { amount: 60, unit: 'second' },
  { amount: 60, unit: 'minute' },
  { amount: 24, unit: 'hour' },
  { amount: 7, unit: 'day' },
  { amount: 4.34524, unit: 'week' },
  { amount: 12, unit: 'month' },
  { amount: Number.POSITIVE_INFINITY, unit: 'year' },
]

/** `4 seconds ago`, `yesterday`, `in 3 months`. */
export function formatRelative(value: DateLike, now: DateLike = Date.now()): string {
  const date = toDate(value)
  const reference = toDate(now)
  if (date === null || reference === null) return ''

  let delta = (date.getTime() - reference.getTime()) / 1000

  for (const division of DIVISIONS) {
    if (Math.abs(delta) < division.amount) {
      return relativeFormat().format(Math.round(delta), division.unit)
    }
    delta /= division.amount
  }

  return relativeFormat().format(Math.round(delta), 'year')
}

function firstLetterOrDigit(word: string): string {
  const match = /[\p{L}\p{N}]/u.exec(word)
  return match === null ? '' : match[0]
}

/**
 * Avatar initials. Two names give two letters, one name gives one, and a
 * display name made entirely of punctuation still gives something to render.
 */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/u).filter((word) => word.length > 0)

  const first = firstLetterOrDigit(words[0] ?? '')
  const last = words.length > 1 ? firstLetterOrDigit(words[words.length - 1] ?? '') : ''
  const combined = `${first}${last}`

  return combined === '' ? '?' : combined.toLocaleUpperCase()
}
